import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { pushSupported, currentSubscription, enablePush, disablePush } from "@/lib/push";
import { trackEvent } from "@/lib/analytics";

const STATES = {
  off: { label: "push_cta", Icon: Bell },
  on: { label: "push_on", Icon: BellRing },
  denied: { label: "push_denied", Icon: BellOff },
};

/** "Remind me every day at 9" toggle. Hidden when the browser cannot do Web Push. */
export default function PushToggle({ className = "", compact = false }) {
  const { t, lang } = useLang();
  const [state, setState] = useState("loading"); // loading | off | on | denied | unsupported
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) { setState("unsupported"); return; }
    if (Notification.permission === "denied") { setState("denied"); return; }
    currentSubscription().then((s) => setState(s ? "on" : "off")).catch(() => setState("off"));
  }, []);

  if (state === "unsupported" || state === "loading") return null;

  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (busy || state === "denied") return;
    setBusy(true);
    try {
      if (state === "on") { await disablePush(); setState("off"); trackEvent("push_disabled"); }
      else { await enablePush(lang); setState("on"); trackEvent("push_enabled", { lang }); }
    } catch (err) {
      if (err.message === "denied") setState("denied");
      trackEvent("push_error", { reason: err.message });
    }
    setBusy(false);
  };

  const on = state === "on";
  const { label, Icon } = STATES[state];
  const Glyph = busy ? Loader2 : Icon;
  return (
    <button type="button" onClick={toggle} disabled={busy || state === "denied"} data-testid="push-toggle" data-state={state} title={on ? t("push_off_hint") : t("push_hint")}
      className={`inline-flex items-center gap-1.5 rounded-full font-medium transition-colors disabled:opacity-60 ${compact ? "px-2.5 py-1 text-[11px]" : "px-4 py-2 text-sm"} ${on ? "bg-oasis/15 text-oasis hover:bg-oasis/25" : "bg-ochre text-white hover:bg-ochre/90"} ${className}`}>
      <Glyph className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} ${busy ? "animate-spin" : ""}`} /> {t(label)}
    </button>
  );
}
